import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Input, Button, Padding } from '../../../../components'
import { colors } from '../../../../constants'

const Wrapper = styled(Padding)`
    &:not(:last-child) {
        border-bottom: 1px solid ${colors.greylighter};
    }
`

const RemoveButton = styled(Button)`
    padding: 3.5px 0;
    width: 50px;
`

const WeekSchedule = ({ timeEnd, timeStart, weekDayEnd, weekDayStart, onChange, onRemove, t }) => {
    const endHours = (timeEnd || '').slice(0, 2)
    const endMinutes = (timeEnd || '').slice(2, 4)
    const startHours = (timeStart || '').slice(0, 2)
    const startMinutes = (timeStart || '').slice(2, 4)

    // Options for the multiple input
    const OptionsWeekSelect = useMemo(
        () => [
            { name: t('monday-short'), value: 1 },
            { name: t('tuesday-short'), value: 2 },
            { name: t('wednesday-short'), value: 3 },
            { name: t('thursday-short'), value: 4 },
            { name: t('friday-short'), value: 5 },
            { name: t('saturday-short'), value: 6 },
            { name: t('sunday-short'), value: 0 },
        ],
        [t]
    )
    // Options for the hours input
    const OptionsHours = useMemo(() => ['', ...new Array(24).fill(null).map((e, i) => (i < 10 ? `0${i}` : i.toString()))], [])
    // Options for the minutes input
    const OptionsMinutes = useMemo(() => ['', ...new Array(60).fill(null).map((e, i) => (i < 10 ? `0${i}` : i.toString()))], [])

    // Value for the input
    const weekDayValue = useMemo(() => {
        // Both days are empty
        if (typeof weekDayEnd !== 'number' && typeof weekDayStart !== 'number') {
            return []
        }

        // Only one day is set
        if (weekDayEnd === weekDayStart) {
            return [weekDayEnd]
        }

        // Length of values array
        const arrayLength = (weekDayEnd < weekDayStart ? weekDayEnd + OptionsWeekSelect.length : weekDayEnd) - weekDayStart + 1 || 0

        // Build the values
        return new Array(arrayLength).fill(null).map((e, i) => (i + weekDayStart) % OptionsWeekSelect.length)
    }, [weekDayEnd, weekDayStart, OptionsWeekSelect])

    /**
     * On change input value
     * @param {Object} event
     */
    const onChangeWeekday = ({ target: { value: newValue } }) => {
        // If no value was previously selected
        if (!weekDayValue.length) {
            const item = newValue[0]
            return onChange({
                timeStart,
                timeEnd,
                weekDayEnd: item,
                weekDayStart: item,
            })
        }

        // If removing all values
        if (!newValue.length) {
            return onChange({
                timeStart,
                timeEnd,
                weekDayEnd: null,
                weekDayStart: null,
            })
        }

        // If start date already selected, set the end date
        if (weekDayValue.length === 1) {
            return onChange({
                timeStart,
                timeEnd,
                weekDayEnd: newValue[newValue.length - 1],
                weekDayStart,
            })
        }

        // Anyother change, just delete the dates
        onChange({
            timeStart,
            timeEnd,
            weekDayEnd: null,
            weekDayStart: null,
        })
    }

    const buildOnChangeTime =
        (field, part) =>
        ({ target: { value } }) => {
            let newTime = null
            if (!value) {
                newTime = null
            } else {
                switch (field) {
                    case 'timeStart':
                        switch (part) {
                            case 'hours':
                                newTime = `${value}${startMinutes || '00'}`
                                break
                            case 'minutes':
                                newTime = `${startHours || '00'}${value}`
                                break
                            default:
                        }
                        break
                    case 'timeEnd':
                        switch (part) {
                            case 'hours':
                                newTime = `${value}${endMinutes || '00'}`
                                break
                            case 'minutes':
                                newTime = `${endHours || '00'}${value}`
                                break
                            default:
                        }
                        break
                    default:
                }
            }

            onChange({
                timeStart,
                timeEnd,
                weekDayEnd,
                weekDayStart,
                [field]: newTime, // This will override the updating field
            })
        }

    return (
        <Wrapper>
            <Padding bottom>
                From&nbsp;
                <Input
                    error={false}
                    inline
                    onChange={buildOnChangeTime('timeStart', 'hours')}
                    size="none"
                    type="select"
                    value={startHours}
                    values={OptionsHours}
                />
                &nbsp;&#58;&nbsp;
                <Input
                    error={false}
                    inline
                    onChange={buildOnChangeTime('timeStart', 'minutes')}
                    size="none"
                    type="select"
                    value={startMinutes}
                    values={OptionsMinutes}
                />
                &nbsp;to&nbsp;
                <Input error={false} inline onChange={buildOnChangeTime('timeEnd', 'hours')} size="none" type="select" value={endHours} values={OptionsHours} />
                &nbsp;&#58;&nbsp;
                <Input
                    error={false}
                    inline
                    onChange={buildOnChangeTime('timeEnd', 'minutes')}
                    size="none"
                    type="select"
                    value={endMinutes}
                    values={OptionsMinutes}
                />
            </Padding>
            <div>
                <Input error={false} inline onChange={onChangeWeekday} size="sm" type="multiple" value={weekDayValue} values={OptionsWeekSelect} />
                <RemoveButton theme="danger" onClick={onRemove}>
                    &times;
                </RemoveButton>
            </div>
        </Wrapper>
    )
}

WeekSchedule.propTypes = {
    timeEnd: PropTypes.string,
    timeStart: PropTypes.string,
    weekDayEnd: PropTypes.number,
    weekDayStart: PropTypes.number,
    onChange: PropTypes.func,
    onRemove: PropTypes.func,
    t: PropTypes.func.isRequired,
}

WeekSchedule.defaultProps = {
    timeEnd: null,
    timeStart: null,
    weekDayEnd: null,
    weekDayStart: null,
    onChange: () => null,
    onRemove: () => null,
}

export default withTranslation()(WeekSchedule)
